import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { User, Mail, Phone, ChevronRight, LogOut, Gift, Copy, CheckCircle, Target, Award, Sparkles, TrendingUp, Share2, Settings, HeartPulse } from 'lucide-react';
import axios from 'axios';

import { API } from '../config/api';
import { MesDroits } from '../components/MesDroits';
const OBJECTIVES = [
  { value: 'tonifier', label: 'Se tonifier' },
  { value: 'perdre', label: 'Perdre du poids' },
  { value: 'renforcer', label: 'Se renforcer' },
  { value: 'detendre', label: 'Se détendre' },
];

export default function Profil() {
  const { isAuthenticated, user, logout } = useAuth();
  const { language, setLanguage, languageNames } = useLanguage();
  const navigate = useNavigate();

  const [profile, setProfile] = useState({ name: '', email: '', phone: '', objective: '' });
  const [stats, setStats] = useState({ totalSessions: 0, thisMonth: 0 });
  const [referral, setReferral] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [referralCodeInput, setReferralCodeInput] = useState('');
  const [applyingCode, setApplyingCode] = useState(false);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return; }
    if (user) setProfile({ name: user.name || '', email: user.email || '', phone: user.phone || '', objective: user.objective || '' });
    fetchAll();
  }, [isAuthenticated, user]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [statsRes, refRes, subRes] = await Promise.all([
        axios.get(`${API}/profile/stats`, { withCredentials: true }),
        axios.get(`${API}/referral/my`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/subscriptions/my`, { withCredentials: true }).catch(() => ({ data: null }))
      ]);
      setStats(statsRes.data);
      setReferral(refRes.data);
      setSubscription(subRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleUpdateProfile = async () => {
    try {
      await axios.put(`${API}/profile`, { name: profile.name, phone: profile.phone, objective: profile.objective }, { withCredentials: true });
      toast.success('Profil mis à jour');
    } catch (e) { toast.error('Erreur'); }
  };

  const copyCode = () => {
    if (referral?.code) {
      navigator.clipboard.writeText(referral.code);
      setCopied(true);
      toast.success('Code copié !');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareCode = () => {
    if (navigator.share && referral?.code) {
      navigator.share({ title: 'PURO Pilates', text: `Rejoins PURO avec mon code ${referral.code} et reçois 1 cours offert !` });
    } else { copyCode(); }
  };

  const applyCode = async () => {
    if (!referralCodeInput.trim()) return;
    setApplyingCode(true);
    try {
      const res = await axios.post(`${API}/referral/apply`, { referral_code: referralCodeInput.trim() }, { withCredentials: true });
      toast.success(res.data.message);
      setReferralCodeInput('');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Code invalide'); }
    finally { setApplyingCode(false); }
  };

  const handleLogout = async () => {
    try { await logout(); navigate('/'); } catch (e) { console.error(e); }
  };

  const memberStatus = subscription?.subscription?.plan_name;

  // Gamification badges
  const badges = [];
  if (stats.totalSessions >= 1) badges.push({ icon: Award, label: 'Première séance complétée', color: '#8C9B86' });
  if (stats.totalSessions >= 10) badges.push({ icon: TrendingUp, label: 'Régularité', color: '#C99A55' });
  if (stats.totalSessions >= 25) badges.push({ icon: Sparkles, label: 'Engagement', color: '#C76542' });

  if (loading) return <div className="flex justify-center py-20"><div className="spinner" /></div>;

  return (
    <div className="min-h-screen bg-[#F5EFE5]">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Avatar & Name */}
        <div className="text-center mb-8 animate-fade-up">
          <div className="w-20 h-20 rounded-full bg-[#C99A55] flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl font-heading font-light text-white">{profile.name?.charAt(0)?.toUpperCase()}</span>
          </div>
          <h1 className="font-heading font-light text-2xl text-[#493329]">{profile.name}</h1>
          <p className="text-sm text-[#7A6455]">Membre PURO</p>
          {memberStatus && (
            <Badge className="mt-2 bg-[#C99A55]/10 text-[#C99A55] rounded-full text-xs px-3 py-1" data-testid="member-status">
              Membre {memberStatus}
            </Badge>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 mb-6 animate-fade-up delay-1">
          <Card className="bg-white border-[#E9DDCC] rounded-2xl">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-heading font-extralight text-[#493329]">{stats.totalSessions}</p>
              <p className="text-xs text-[#7A6455] mt-0.5">séances totales</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-[#E9DDCC] rounded-2xl">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-heading font-extralight text-[#493329]">{stats.thisMonth}</p>
              <p className="text-xs text-[#7A6455] mt-0.5">ce mois-ci</p>
            </CardContent>
          </Card>
        </div>

        {/* Gamification Badges */}
        {badges.length > 0 && (
          <Card className="bg-white border-[#E9DDCC] rounded-3xl mb-6 animate-fade-up delay-2" data-testid="badges-section">
            <CardContent className="p-5">
              <p className="text-xs uppercase tracking-widest text-[#C99A55] mb-3">Vos récompenses</p>
              <div className="flex gap-3">
                {badges.map((badge, i) => (
                  <div key={i} className="flex items-center gap-2 bg-[#F0E8D9] rounded-full px-3 py-2">
                    <badge.icon className="w-4 h-4" style={{ color: badge.color }} strokeWidth={1.5} />
                    <span className="text-xs text-[#493329]">{badge.label}</span>
                  </div>
                ))}
              </div>
              {stats.totalSessions > 0 && (
                <p className="text-xs text-[#C99A55] mt-3 italic">Bravo pour votre progression !</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Ma santé */}
        <Link to="/ma-sante" className="block mb-6" data-testid="ma-sante-link">
          <Card className="bg-white border-[#E9DDCC] rounded-3xl hover:border-[#C99A55] transition-colors">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-11 h-11 rounded-2xl bg-[#C99A55]/15 flex items-center justify-center flex-shrink-0">
                <HeartPulse className="w-5 h-5 text-[#C99A55]" strokeWidth={1.6} />
              </div>
              <div className="flex-1">
                <h2 className="font-heading font-light text-lg text-[#493329]">Ma fiche santé</h2>
                <p className="text-xs text-[#9A8B78]">Blessures, grossesse, antécédents — pour des séances sûres et adaptées.</p>
              </div>
              <ChevronRight className="w-5 h-5 text-[#C99A55]" />
            </CardContent>
          </Card>
        </Link>

        {/* Referral */}
        <Card className="bg-[#C99A55] text-white rounded-3xl mb-6 animate-fade-up delay-3" data-testid="referral-section">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center">
                <Gift className="w-5 h-5 text-white" strokeWidth={1.5} />
              </div>
              <div>
                <h2 className="font-heading font-light text-lg">Invitez une amie. Progressez ensemble.</h2>
                <p className="text-white/60 text-sm">Offrez 1 séance. Recevez 1 séance.</p>
              </div>
            </div>

            {referral && (
              <>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 mb-4">
                  <p className="text-white/50 text-xs uppercase tracking-widest mb-1">Votre code</p>
                  <div className="flex items-center justify-between">
                    <span className="font-heading text-xl tracking-[0.2em]" data-testid="referral-code">{referral.code}</span>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={copyCode} className="text-white hover:bg-white/15 rounded-full h-8 w-8 p-0" data-testid="copy-referral-btn">
                        {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={shareCode} className="text-white hover:bg-white/15 rounded-full h-8 w-8 p-0" data-testid="share-referral-btn">
                        <Share2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-2xl font-heading font-extralight">{referral.referrals_count}</p>
                    <p className="text-xs text-white/50">invitation{referral.referrals_count > 1 ? 's' : ''} utilisée{referral.referrals_count > 1 ? 's' : ''}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-heading font-extralight">{referral.credits_earned}</p>
                    <p className="text-xs text-white/50">cours offert{referral.credits_earned > 1 ? 's' : ''}</p>
                  </div>
                </div>
              </>
            )}

            <div className="border-t border-white/15 pt-4">
              <p className="text-white/50 text-xs mb-2">Vous avez un code de parrainage ?</p>
              <div className="flex gap-2">
                <Input value={referralCodeInput} onChange={(e) => setReferralCodeInput(e.target.value.toUpperCase())} placeholder="PUROXXXXXX"
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/30 rounded-xl h-10" data-testid="referral-code-input" />
                <Button onClick={applyCode} disabled={applyingCode || !referralCodeInput.trim()}
                  className="bg-white text-[#C99A55] hover:bg-white/90 rounded-xl h-10 px-5 btn-premium" data-testid="apply-referral-btn">
                  Appliquer
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Profile Info */}
        <Card className="bg-white border-[#E9DDCC] rounded-3xl mb-6 animate-fade-up delay-4">
          <CardContent className="p-6">
            <p className="text-xs uppercase tracking-widest text-[#C99A55] mb-4">Mes informations</p>
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-[#7A6455]">Nom</Label>
                <Input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                  className="bg-[#F5EFE5] border-[#E9DDCC] rounded-xl mt-1" data-testid="profile-name-input" />
              </div>
              <div>
                <Label className="text-xs text-[#7A6455]">Email</Label>
                <Input value={profile.email} disabled className="bg-[#F0E8D9] border-[#E9DDCC] rounded-xl mt-1 text-[#7A6455]" />
              </div>
              <div>
                <Label className="text-xs text-[#7A6455]">Téléphone</Label>
                <Input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                  className="bg-[#F5EFE5] border-[#E9DDCC] rounded-xl mt-1" placeholder="06 12 34 56 78" data-testid="profile-phone-input" />
              </div>
              <div>
                <Label className="text-xs text-[#7A6455]">Mon objectif</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {OBJECTIVES.map(obj => (
                    <button key={obj.value} onClick={() => setProfile({ ...profile, objective: obj.value })}
                      className={`p-3 rounded-xl text-sm text-left transition-all ${
                        profile.objective === obj.value
                          ? 'bg-[#C99A55] text-white'
                          : 'bg-[#F0E8D9] text-[#493329] hover:bg-[#E9DDCC]'
                      }`} data-testid={`objective-${obj.value}`}>
                      {obj.label}
                    </button>
                  ))}
                </div>
              </div>
              <Button onClick={handleUpdateProfile} className="w-full bg-[#C99A55] hover:bg-[#B08A4F] text-white rounded-xl h-11 btn-premium" data-testid="save-profile-btn">
                Enregistrer
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Settings */}
        <Card className="bg-white border-[#E9DDCC] rounded-3xl mb-6 animate-fade-up delay-5">
          <CardContent className="p-6">
            <p className="text-xs uppercase tracking-widest text-[#C99A55] mb-4">Paramètres</p>
            <div className="space-y-1">
              <div className="flex items-center justify-between py-3 border-b border-[#E9DDCC]">
                <span className="text-sm text-[#493329]">Langue</span>
                <select value={language} onChange={(e) => setLanguage(e.target.value)}
                  className="bg-[#F0E8D9] border-none rounded-lg px-3 py-1.5 text-sm text-[#493329]">
                  {Object.entries(languageNames || { fr: 'Français', en: 'English', es: 'Español' }).map(([code, name]) => (
                    <option key={code} value={code}>{name}</option>
                  ))}
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Help */}
        <Card className="bg-white border-[#E9DDCC] rounded-3xl mb-6 animate-fade-up delay-6">
          <CardContent className="p-6">
            <p className="text-xs uppercase tracking-widest text-[#C99A55] mb-4">Aide</p>
            <div className="space-y-0.5">
              <Link to="/contact" className="flex items-center justify-between w-full py-3 border-b border-[#E9DDCC] hover:bg-[#F0E8D9] -mx-1 px-1 rounded-lg transition-colors">
                <span className="text-sm text-[#493329]">Nous contacter</span>
                <ChevronRight className="w-4 h-4 text-[#E9DDCC]" strokeWidth={1.5} />
              </Link>
              <Link to="/nos-abonnements" className="flex items-center justify-between w-full py-3 border-b border-[#E9DDCC] hover:bg-[#F0E8D9] -mx-1 px-1 rounded-lg transition-colors">
                <span className="text-sm text-[#493329]">Nos formules</span>
                <ChevronRight className="w-4 h-4 text-[#E9DDCC]" strokeWidth={1.5} />
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Mes données & droits (RGPD) */}
        <MesDroits />

        {/* Logout */}
        <Button onClick={handleLogout} variant="ghost" className="w-full text-[#C76542] hover:bg-[#C76542]/10 rounded-2xl h-12" data-testid="logout-btn">
          <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} /> Se déconnecter
        </Button>
      </div>
    </div>
  );
}
